import React, { useState } from "react";
import {
  Menu,
  AlertTriangle,
  FilterIcon,
  PlusIcon,
  UploadIcon,
  PlusCircleIcon,
  PencilIcon,
  X,
  Loader2,
  CalendarIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { LoadingFallback } from "@/components/loading-fallback";
import { cn } from "@/lib/utils";
import { MyAccountDropdown } from "@/components/my-account-dropdown";
import {
  TiktokDiscoveryPeriod,
  useTiktokDiscoveryPeriodList,
} from "@/hooks/tiktokDiscoveryPeriod.hooks";
import { TiktokDiscoveryPeriodExcerptView } from "./tiktok-discovery-period-excerpt-view";
import moment from "moment";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { MyAdminAccountDropdown } from "@/components/my-admin-account-dropdown";

const statusConfig: Record<string, { name: string; color: string }> = {
  IN_PROGRESS: {
    name: "In Progress",
    color: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100",
  },
  WAITING: {
    name: "Waiting",
    color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100",
  },
};

export const TiktokDiscoveryPeriods = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPeriod, setSelectedPeriod] =
    useState<TiktokDiscoveryPeriod | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const pageSize = 20;

  const { tiktokDiscoveryPeriods, isLoading, error, isFetched } =
    useTiktokDiscoveryPeriodList(
      currentPage,
      pageSize,
      undefined,
      undefined,
      selectedStatuses.length > 0 ? selectedStatuses : undefined
    );

  const handleStatusChange = (status: string) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
  };

  return (
    <div className="min-h-screen flex-1 flex flex-col overflow-hidden">
      <main className="flex-1 bg-background overflow-y-auto min-h-screen">
        <header className="border-b pt-6 pb-2 shadow-sm h-auto flex flex-wrap items-center gap-4 px-4 md:px-6">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <h2 className="text-xl font-semibold text-foreground">
              TikTok Discovery Periods
              {isFetched &&
                (tiktokDiscoveryPeriods && tiktokDiscoveryPeriods.totalCount
                  ? ` (${tiktokDiscoveryPeriods.totalCount})`
                  : " (0)")}
            </h2>
            <Dialog open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <FilterIcon size={14} />
                  <span>Filters</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Filter Discovery Periods</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-4">
                    <Label className="text-base">Status</Label>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(statusConfig).map(([status, config]) => (
                        <div
                          key={status}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={status}
                            checked={selectedStatuses.includes(status)}
                            onCheckedChange={() => handleStatusChange(status)}
                          />
                          <Label htmlFor={status}>{config.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => setIsFilterOpen(false)}>
                    Apply Filters
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="flex items-center space-x-2 w-full md:w-auto md:ml-auto">
            <MyAdminAccountDropdown />
          </div>
        </header>

        <div className="mt-6 px-6">
          <LoadingFallback loading={isLoading}>
            {error ? (
              <div className="text-red-500 font-semibold">
                Error: {error.message}
              </div>
            ) : (
              <>
                <div className="bg-background rounded-xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="font-semibold text-foreground/70">
                          Shop ID
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Shop Name
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Seller Account
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Organisation
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Status
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Start Date
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          End Date
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Created/Updated At
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tiktokDiscoveryPeriods &&
                      tiktokDiscoveryPeriods.items &&
                      tiktokDiscoveryPeriods.items.length > 0 ? (
                        tiktokDiscoveryPeriods.items.map((period) => (
                          <TableRow
                            key={period.id}
                            className="hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => setSelectedPeriod(period)}
                          >
                            <TableCell>{period.shop.id}</TableCell>
                            <TableCell>{period.shop.name}</TableCell>
                            <TableCell>
                              {period.shopAccount.sellerName}
                            </TableCell>
                            <TableCell>{period.organisation.name}</TableCell>
                            <TableCell>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  statusConfig[period.status].color
                                }`}
                              >
                                {period.status === "IN_PROGRESS" && (
                                  <Loader2 className="h-4 w-4 mr-1 inline-block animate-spin" />
                                )}
                                {statusConfig[period.status].name}
                              </span>
                            </TableCell>
                            <TableCell>
                              {moment(period.startDate).format(
                                "MMM D, YYYY h:mm A"
                              )}
                            </TableCell>
                            <TableCell>
                              {period.endDate
                                ? moment(period.endDate).format(
                                    "MMM D, YYYY h:mm A"
                                  )
                                : "-"}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="text-sm text-foreground/40">
                                  <PlusCircleIcon
                                    size={14}
                                    className="mr-1.5 inline-block"
                                  />
                                  {moment(period.createdAt).format(
                                    "MMM D, YYYY h:mm A"
                                  )}
                                </span>
                                <span className="text-sm text-foreground/70">
                                  <PencilIcon
                                    size={14}
                                    className="mr-1.5 inline-block"
                                  />
                                  {moment(period.updatedAt).format(
                                    "MMM D, YYYY h:mm A"
                                  )}
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            className="text-center py-8 text-foreground/60"
                          >
                            No discovery periods found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {tiktokDiscoveryPeriods &&
                  tiktokDiscoveryPeriods.totalPages > 0 && (
                    <Pagination className="my-8 flex justify-center">
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() =>
                              setCurrentPage((prev) => Math.max(prev - 1, 1))
                            }
                            className="cursor-pointer rounded-full bg-background border hover:bg-muted/50 transition-colors"
                          />
                        </PaginationItem>
                        {Array.from(
                          { length: tiktokDiscoveryPeriods.totalPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => setCurrentPage(page)}
                              isActive={
                                page === tiktokDiscoveryPeriods.currentPage
                              }
                              className={cn(
                                "rounded-full cursor-pointer",
                                page === tiktokDiscoveryPeriods.currentPage
                                  ? "bg-blue-500/50"
                                  : "bg-background hover:bg-muted/50",
                                "border transition-colors"
                              )}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setCurrentPage((prev) => prev + 1)}
                            className="cursor-pointer rounded-full bg-background border hover:bg-muted/50 transition-colors"
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  )}

                <TiktokDiscoveryPeriodExcerptView
                  open={!!selectedPeriod}
                  setOpen={setSelectedPeriod}
                  period={selectedPeriod}
                />
              </>
            )}
          </LoadingFallback>
        </div>
      </main>
    </div>
  );
};
